import { useDisplayScale } from '../layouts/useDisplayScale.js'

/**
 * How a round is going, as a race.
 *
 * Lives as hearts and a tally of ten are honest and dull. This is the same
 * information drawn — and it is the *same* information: every number here
 * comes off the game's own view, so a track can never disagree with the board
 * above it. Nothing is kept when the round ends. It is not a score.
 *
 * Deliberately silent to a screen reader. Each game screen has exactly one
 * live region (see Announce) and it already says how the round is going in
 * words — "four of seven letters found, five lives left" — which is the same
 * news, better told. A track that announced as well would be a second region
 * racing the first.
 */
export interface Racer {
  /** Identity, for React and for nothing else. */
  readonly key: string
  /** Only ever read by somebody debugging; the track is aria-hidden. */
  readonly name: string
  readonly picture: string
  /** Steps taken so far, and how many reach the goal. */
  readonly steps: number
  readonly of: number
  /** The lane's colour, so two racers are not two identical rails. */
  readonly tint: string
}

/** Where along its lane a racer sits, as a fraction. */
function progressOf(racer: Racer): number {
  if (racer.of <= 0) return 0
  return Math.min(1, Math.max(0, racer.steps / racer.of))
}

/**
 * Which way a racer travels.
 *
 * Everywhere but the television both racers run left to right in their own
 * lanes. On the television, where Daddy runs, the goal sits between them and
 * he comes back the other way — so the same lane needs a racer that starts at
 * its right-hand end.
 *
 * The distinction is only about where the picture is drawn. A racer's progress
 * is still `steps / of`, and Daddy's `of` is three times hers, so three wrong
 * answers still move him exactly as far as one right answer moves her. That
 * ratio is the design of the race and nothing here may touch it: turning it
 * into "whoever is nearer the middle" would make a game she is struggling with
 * visibly worse, which is the one thing these tracks exist not to do.
 */
type Heading = 'rightwards' | 'leftwards'

function Lane({ racer, heading = 'rightwards' }: { racer: Racer; heading?: Heading }) {
  const progress = progressOf(racer)
  const along = heading === 'rightwards' ? progress : 1 - progress

  return (
    /*
     * Marked for measuring, because a racer once sat outside its lane at
     * television scale and nothing could see it: jsdom gives every element a
     * bounding box of zero, so "is this inside that" is a question it cannot
     * be asked. The browser suite asks it, and needs to be able to find the
     * lane and the racer without depending on class names.
     *
     * One per lane, not one per race — the name says so, because a measurement
     * that compared every racer against the first lane's bounds would be right
     * only for as long as the lanes stayed the same width.
     */
    <div
      data-race-lane
      /*
       * `w-full`, never `flex-1`.
       *
       * The lanes stack in a **column**, and in a column `flex: 1 1 0%` sets
       * the basis on the *height* — so `flex-1` quietly overrode the height
       * beside it and, with nothing definite above, resolved to zero. The
       * racer is absolutely positioned, so it did not hold the lane open
       * either: it overflowed and landed on its neighbour. Two racers eight
       * pixels apart, overlapping by thirty-six, measured on a phone.
       *
       * It looked right on the television only because the container there
       * has a definite height for `flex-1` to divide.
       */
      className="relative h-(--size-tap) w-full min-w-0"
    >
      {/* The lane itself. Dashed, so the steps are countable rather than a
          smooth bar — a bar reads as a percentage, which is a mark. */}
      <div
        className={`absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full ${racer.tint}`}
      />

      {/*
        The track the racer's centre actually travels along: the lane, inset by
        half a racer at each end. That way 0% puts its left edge on the start
        line and 100% puts its right edge on the goal, with no arithmetic in
        the position itself.

        This used to compensate with a hardcoded rem offset — half of
        `--size-tap` written out as a number. Which was right until the
        television scale, where that token is 5rem rather than 2.75rem, so the
        racer sat a centimetre off the lane at both ends on the one screen this
        whole feature is meant for. Insetting with the token itself cannot
        drift from it.
      */}
      <div className="absolute inset-y-0 left-[calc(var(--size-tap)/2)] right-[calc(var(--size-tap)/2)]">
        <img
          data-racer
          src={racer.picture}
          alt=""
          className="absolute top-1/2 size-(--size-tap) -translate-x-1/2 -translate-y-1/2 rounded-full transition-[left] duration-500 ease-out"
          style={{ left: `${along * 100}%` }}
        />
      </div>
    </div>
  )
}

export interface RaceTrackProps {
  readonly racers: readonly Racer[]
  /** What is being run towards. */
  readonly goal: string
}

/* Alt is empty rather than descriptive: the whole track is hidden from
   assistive technology, so an alt here would only mislead a reader of this
   file into thinking somebody hears it. */
function Goal({ src }: { src: string }) {
  /*
   * Half as big again as a racer.
   *
   * At the same size it read as three teddies in a row rather than two of them
   * running towards something — the thing being run towards should be the
   * bigger of the two.
   *
   * At every scale, not only the television where it was noticed. It is a fact
   * about what a race looks like rather than about how much room there is, and
   * `--size-tap` already carries the scale, so the proportion holds everywhere.
   */
  return (
    <img
      src={src}
      alt=""
      className="size-[calc(var(--size-tap)*1.5)] shrink-0 rounded-(--radius-control)"
    />
  )
}

export function RaceTrack({ racers, goal }: RaceTrackProps) {
  const scale = useDisplayScale()

  const [first, second] = racers

  /*
   * On the television, two racers run at each other rather than alongside.
   *
   * Two stacked lanes take twice the height, and they say the wrong thing
   * besides: side by side is a running race, where what is actually happening
   * is that her right answers and her mistakes are pulling the same rope.
   * Converging on one goal is the truer picture and the one a child reads
   * instantly.
   *
   * Only where there are exactly two. A single racer walking to a carrot has
   * nothing to converge with, and three would need a goal in a place that
   * means something.
   */
  if (scale === 'tv' && first && second && racers.length === 2) {
    return (
      <div aria-hidden="true" className="flex w-full items-center gap-3">
        <Lane racer={first} />
        <Goal src={goal} />
        <Lane racer={second} heading="leftwards" />
      </div>
    )
  }

  return (
    <div
      // Silent. The screen's one live region already says this in words.
      aria-hidden="true"
      className="flex w-full items-center gap-3"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {racers.map((racer) => (
          <Lane key={racer.key} racer={racer} />
        ))}
      </div>

      <Goal src={goal} />
    </div>
  )
}
